const COOKIE_PREFERENCES_HASH = 'cookie-preferences' as const;

type OpenResult = { opened: true; via: string } | { opened: false; via: string; reason?: string };

function tryCall(fn: unknown, via: string): OpenResult | null {
    if (typeof fn !== 'function') return null;
    try {
        (fn as (...args: unknown[]) => unknown)();
        return { opened: true, via };
    } catch (err) {
        return { opened: false, via, reason: err instanceof Error ? err.message : String(err) };
    }
}

function tryClick(selector: string, via: string): OpenResult {
    const el = document.querySelector(selector);
    if (!el) return { opened: false, via, reason: `No element matched: ${selector}` };
    if (!(el instanceof HTMLElement)) return { opened: false, via, reason: `Element not HTMLElement: ${selector}` };
    el.click();
    return { opened: true, via };
}

function openViaTrusteGlobal(): OpenResult | null {
    const w = window as unknown as {
        truste?: Record<string, unknown>;
    };

    const truste = w.truste as any;
    if (!truste) return null;

    // GIA TrustArc notice (v1.7) wires "Cookie Preferences" / Manage Settings to
    // `truste.eu.clickListener(0)` — not showConsentManager / #teconsent a.
    const euClickListener = truste.eu?.clickListener;
    if (typeof euClickListener === 'function') {
        const res = tryCall(() => euClickListener.call(truste.eu, 0), 'window.truste.eu.clickListener(0)');
        if (res?.opened) return res;
    }

    const attempts: Array<{ fn: unknown; via: string }> = [
        { fn: truste.showConsentManager, via: 'window.truste.showConsentManager()' },
        { fn: truste.showConsentTool, via: 'window.truste.showConsentTool()' },
        { fn: truste.openConsentManager, via: 'window.truste.openConsentManager()' },
        { fn: truste.openConsentTool, via: 'window.truste.openConsentTool()' },
        { fn: truste.consentManager?.open, via: 'window.truste.consentManager.open()' },
        { fn: truste.ccm?.open, via: 'window.truste.ccm.open()' },
    ];

    for (const { fn, via } of attempts) {
        const res = tryCall(fn, via);
        if (res && res.opened) return res;
    }

    return { opened: false, via: 'window.truste', reason: 'No known TrustArc open method found' };
}

export function openTrustArcCookiePreferences(): OpenResult {
    const globalRes = openViaTrusteGlobal();
    if (globalRes?.opened) return globalRes;

    const clickRes =
        tryClick('#truste-show-consent', '#truste-show-consent (Manage Settings)') ??
        tryClick('#teconsent #truste-show-consent', '#teconsent #truste-show-consent') ??
        tryClick('#teconsent a', '#teconsent a') ??
        tryClick('#teconsent button', '#teconsent button') ??
        tryClick('#teconsent [role="button"]', '#teconsent [role="button"]');

    if (clickRes.opened) return clickRes;

    return globalRes ?? clickRes;
}

export function isCookiePreferencesHash(hash: string): boolean {
    return hash.replace(/^#/, '') === COOKIE_PREFERENCES_HASH;
}

export function getCookiePreferencesHash(): string {
    return `#${COOKIE_PREFERENCES_HASH}`;
}

export async function openTrustArcCookiePreferencesWithRetry(opts?: {
    attempts?: number;
    delayMs?: number;
}): Promise<OpenResult> {
    const attempts = Math.max(1, opts?.attempts ?? 5);
    const delayMs = Math.max(0, opts?.delayMs ?? 200);

    let last: OpenResult = { opened: false, via: 'retry', reason: 'Not attempted yet' };
    for (let i = 0; i < attempts; i++) {
        last = openTrustArcCookiePreferences();
        if (last.opened) return last;
        if (i < attempts - 1) await new Promise(r => setTimeout(r, delayMs));
    }
    return last;
}
